<template>
  <div class="brand-studio" dir="rtl">
    <header class="brand-studio__top">
      <div>
        <p class="brand-studio__eyebrow">مدیریت برندها</p>
        <h1 class="brand-studio__title">{{ title }}</h1>
      </div>
      <router-link :to="{ name: 'Brand' }" class="brand-studio__chip">
        بازگشت به برندها <b-icon icon="arrow-left"/>
      </router-link>
    </header>

    <form class="brand-studio__layout" @submit.prevent="onSubmit">
      <section class="brand-card brand-area-main">
        <div class="brand-card__head">
          <h2 class="brand-card__title">
            <span class="brand-card__icon"><b-icon icon="card-heading"/></span>
            اطلاعات برند
          </h2>
        </div>
        <div class="brand-card__body">
          <div class="brand-grid">
            <form-group class="brand-field" :validations="$v.form.name">
              <div slot-scope="{ errors, invalid }">
                <label class="brand-label">نام</label>
                <input v-model="form.name" type="text" @input="onNameInput" class="form-control brand-input" :class="{ invalid }" placeholder="نام برند">
                <form-errors-manager :errors="errors"/>
              </div>
            </form-group>

            <form-group class="brand-field" :validations="$v.form.slug">
              <div slot-scope="{ errors, invalid }">
                <label class="brand-label">slug</label>
                <input v-model="form.slug" type="text" dir="ltr" @input="onSlugInput" class="form-control brand-input" :class="{ invalid }" placeholder="brand-slug">
                <form-errors-manager :errors="errors"/>
              </div>
            </form-group>
          </div>


          <form-group class="brand-field" :validations="$v.form.description">
            <div slot-scope="{ errors, invalid }">
              <label class="brand-label">توضیحات</label>
              <textarea v-model="form.description" class="form-control brand-input" rows="6"
                        placeholder="توضیحات (اختیاری)"></textarea>
              <form-errors-manager :errors="errors"/>
            </div>
          </form-group>
        </div>
      </section>

      <aside class="brand-side brand-area-side">
        <section class="brand-card">
          <div class="brand-card__head">
            <h2 class="brand-card__title">
              <span class="brand-card__icon"><b-icon icon="image"/></span>
              تصویر برند
            </h2>
          </div>
          <div class="brand-card__body">
            <form-group :validations="$v.form.preview">
              <div slot-scope="{ errors, invalid }">
                <input id="brand-image-input" type="file" accept="image/*" class="d-none" @change="onFileChange"/>
                <label for="brand-image-input" class="brand-image-picker" :class="{ 'is-empty': !form.preview, invalid }">
                  <img v-if="form.preview" :src="form.preview" alt="">
                  <template v-else>
                    <b-icon icon="cloud-arrow-up" class="brand-image-picker__icon"/>
                    <span>انتخاب تصویر (اختیاری)</span>
                  </template>
                  <span v-if="form.preview" class="brand-image-picker__change"><b-icon icon="camera"/> تغییر تصویر</span>
                </label>
                <form-errors-manager :errors="errors"/>
              </div>
            </form-group>
          </div>
        </section>

        <section class="brand-card">
          <div class="brand-card__head brand-card__head--split">
            <h2 class="brand-card__title">
              <span class="brand-card__icon"><b-icon icon="translate"/></span>
              زبان
            </h2>
            <span class="brand-lang-status" :class="{ 'is-set': !!form.language_id }">
              <span class="brand-lang-status__dot"></span>
              {{ currentLanguage ? currentLanguage.name : 'انتخاب نشده' }}
            </span>
          </div>
          <div class="brand-card__body">
            <form-group :validations="$v.form.language_id">
              <div slot-scope="{ errors }">
                <div class="brand-lang-tabs" role="radiogroup">
                  <button v-for="item in languages" :key="item.id" type="button" :id="`language_item_${item.id}`"
                          class="brand-lang-tab" :class="{ 'is-active': String(form.language_id) === String(item.id) }"
                          @click="form.language_id = item.id">
                    <b-icon v-if="String(form.language_id) === String(item.id)" icon="check2"/>
                    {{ item.name }}
                  </button>
                </div>
                <form-errors-manager :errors="errors"/>
              </div>
            </form-group>

            <template v-if="isEdit && otherLanguages.length">
              <p class="brand-lang-hint">ترجمه این برند در زبان دیگر:</p>
              <div class="brand-lang-translations">
                <router-link v-for="item in otherLanguages" :key="'tr-' + item.id" class="brand-lang-add"
                             :to="{ name: 'CreateBrand', query: { lang: item.id, name: form.name, description: form.description || undefined } }">
                  <b-icon icon="plus-circle"/> افزودن نسخه {{ item.name }}
                </router-link>
              </div>
            </template>
          </div>
        </section>
      </aside>

      <div class="brand-area-submit">
        <button type="submit" class="brand-submit">
          <b-icon icon="check2-circle"/> ثبت
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import FormGroup from "../../validator/FormGroup";
import FormErrorsManager from "../../validator/FormErrorsManager";
import {required} from "vuelidate/lib/validators";
import {slug} from "@moeinmdn/mixitweb-front-modules/src/plugin/class/VuelidateCustomValidatoins";
import {mapGetters} from "vuex";

export default {
  name: "CreateLayout",
  props: ['title', 'submitFunction'],
  components: {FormGroup, FormErrorsManager},
  computed: {
    ...mapGetters({
      languages: "Language/getLanguages"
    }),
    isEdit() {
      return this.$route.params.slug !== undefined
    },
    currentLanguage() {
      return (this.languages || []).find(item => String(item.id) === String(this.form.language_id)) || null
    },
    otherLanguages() {
      return (this.languages || []).filter(item => String(item.id) !== String(this.form.language_id))
    }
  },
  data() {
    return {
      form: {
        name: '',
        slug: '',
        image: [],
        preview: '',
        description: '',
        language_id: ''
      },
      slugAuto: true,
    }
  },
  validations: {
    form: {
      name: {required},
      slug: {required, slug},
      preview: {},
      language_id: {required},
      description: {},
    }
  },
  methods: {
    invalid() {
      this.$v.$touch();
      return !this.$v.$invalid;
    },
    onSubmit() {
      if (this.invalid()) {
        const form = this.form
        this.submitFunction(form)
      }
    },
    // same slugify logic as the product quick-create forms (QuickCategoryCreate / QuickBrandCreate)
    makeSlug(name) {
      if (!name) return ''
      let link = String(name).trim().toLowerCase()
      link = link.replace(/ +/g, "-")
      link = link.replace(/[^\wآابپتثجچحخدذرزژسشصضطظعغفقکگلمنوهی]+/g, "-")
      return link.replace(/-+/g, "-").replace(/^-|-$/g, "")
    },
    onNameInput() {
      if (this.slugAuto) this.form.slug = this.makeSlug(this.form.name)
    },
    onSlugInput() {
      // manual edit stops auto sync; clearing the slug turns it back on
      this.slugAuto = !this.form.slug
    },
    onFileChange(e) {
      const file = e.target.files[0];
      if (!file) return
      this.form.image = file;
      this.form.preview = URL.createObjectURL(file);
    },
  },
  mounted() {
    const routerSlug = this.$route.params.slug;
    let language_id = this.findAttributeOnSiteInfo('default_language')[0].id
    if (routerSlug !== undefined) {
      this.form.slug = routerSlug;
      const form = this.form
      this.$store.dispatch('Brand/showBrand', form).then(async res => {
        const brand = res.data.data;
        this.form.name = brand.name;
        this.form.id = brand.id;
        this.form.slug = brand.slug;
        this.slugAuto = !brand.slug
        this.form.description = brand.description;
        this.form.preview = brand.image;
        this.form.image = []
        language_id = await brand.language_id;
        document.getElementById(`language_item_${language_id}`).click();

      }).catch(err => {
        this.$router.push({name: 'Brand'})
      })
    } else {
      // "add translation" from the edit page passes ?lang=&name=&description=
      const query = this.$route.query || {}
      if (query.lang) language_id = query.lang
      if (query.name) {
        this.form.name = query.name
        this.form.slug = this.makeSlug(query.name)
      }
      if (query.description) this.form.description = query.description
      const languageButton = document.getElementById(`language_item_${language_id}`)
      if (languageButton) languageButton.click(); else this.form.language_id = language_id
    }
  },
}
</script>

<style scoped>
.brand-studio {
  --dash-primary: #eb3d60;
  --dash-primary-soft: rgba(235, 61, 96, 0.1);
  --line: rgba(30, 30, 30, 0.08);
  --ink: #1e1e1e;
  --ink-soft: #424242;
  padding: 8px 4px 24px;
}

.brand-studio__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  padding: 1.1rem 1.4rem;
  border-radius: 20px;
  background: linear-gradient(135deg, #1e1e1e 0%, #3a3a3a 55%, #eb3d60 140%);
  color: #fff;
}

.brand-studio__eyebrow {
  margin: 0 0 0.2rem;
  font-size: 0.8rem;
  opacity: 0.8;
}

.brand-studio__title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
}

.brand-studio__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.95rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-weight: 700;
  font-size: 0.85rem;
  text-decoration: none;
}

.brand-studio__chip:hover {
  background: rgba(255, 255, 255, 0.24);
  color: #fff;
}

.brand-studio__layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  grid-template-areas:
    "main side"
    "main submit";
  grid-template-rows: auto 1fr;
  gap: 1rem;
  align-items: start;
}

.brand-area-main {
  grid-area: main;
}

.brand-area-side {
  grid-area: side;
}

.brand-area-submit {
  grid-area: submit;
}

.brand-side {
  display: grid;
  gap: 1rem;
}

.brand-card__head--split {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.brand-lang-status {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  background: #f3f3f5;
  color: #8a8a96;
  font-size: 0.74rem;
  font-weight: 700;
  white-space: nowrap;
}

.brand-lang-status.is-set {
  background: rgba(25, 135, 84, 0.1);
  color: #198754;
}

.brand-lang-status__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

.brand-lang-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px;
  border-radius: 12px;
  background: #f3f3f5;
  border: 1px solid var(--line);
}

.brand-lang-tab {
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  padding: 0.45rem 0.7rem;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--ink-soft);
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
}

.brand-lang-tab.is-active {
  background: #fff;
  color: var(--dash-primary);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.brand-lang-hint {
  margin: 0.85rem 0 0.4rem;
  color: #8a8a96;
  font-size: 0.76rem;
  font-weight: 700;
}

.brand-lang-translations {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.brand-lang-add {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.7rem;
  border: 1px dashed rgba(235, 61, 96, 0.4);
  border-radius: 999px;
  color: var(--dash-primary);
  font-size: 0.76rem;
  font-weight: 700;
  text-decoration: none;
}

.brand-lang-add:hover {
  background: var(--dash-primary-soft);
  color: var(--dash-primary);
  text-decoration: none;
}

.brand-card {
  border: 1px solid var(--line);
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(30, 30, 30, 0.05);
  overflow: hidden;
}

.brand-card__head {
  padding: 0.85rem 1rem;
  border-bottom: 1px solid var(--line);
  background: linear-gradient(120deg, rgba(235, 61, 96, 0.08), transparent 46%), #fff;
}

.brand-card__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
  color: var(--ink);
}

.brand-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.7rem;
  height: 1.7rem;
  border-radius: 10px;
  background: rgba(235, 61, 96, 0.12);
  color: var(--dash-primary);
  font-size: 0.95rem;
}

.brand-card__body {
  padding: 1rem;
}

.brand-card__body > * + * {
  margin-top: 1rem;
}

.brand-card__body >>> .form-group {
  margin-bottom: 0;
}

.brand-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

.brand-label {
  display: block;
  margin-bottom: 0.4rem;
  font-weight: 700;
  color: var(--ink-soft);
  font-size: 0.85rem;
}

.brand-input {
  border-radius: 12px !important;
  border-color: var(--line);
}

.brand-input:focus {
  border-color: var(--dash-primary);
  box-shadow: 0 0 0 3px rgba(235, 61, 96, 0.12);
}

.brand-image-picker {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
  aspect-ratio: 1 / 1;
  margin: 0;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  background: #f7f7f8;
}

.brand-image-picker.is-empty {
  border: 1.5px dashed rgba(30, 30, 30, 0.18);
  color: #6b6b6b;
  font-weight: 700;
  font-size: 0.85rem;
}

.brand-image-picker.is-empty:hover {
  border-color: var(--dash-primary);
  color: var(--dash-primary);
  background: rgba(235, 61, 96, 0.04);
}

.brand-image-picker.invalid {
  border-color: #dc3545;
}

.brand-image-picker__icon {
  font-size: 2rem;
}

.brand-image-picker img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.brand-image-picker__change {
  position: absolute;
  bottom: 0.6rem;
  right: 50%;
  transform: translateX(50%);
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  background: rgba(30, 30, 30, 0.7);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

.brand-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
  min-height: 50px;
  border: 0;
  border-radius: 999px;
  background: var(--dash-primary);
  color: #fff;
  font-weight: 800;
  box-shadow: 0 10px 24px rgba(235, 61, 96, 0.32);
  cursor: pointer;
}

.brand-submit:hover {
  filter: brightness(0.95);
}

@media (max-width: 991.98px) {
  .brand-studio__layout {
    grid-template-columns: 1fr;
    grid-template-areas:
      "side"
      "main"
      "submit";
    grid-template-rows: auto;
  }

  .brand-image-picker {
    max-width: 220px;
    margin: 0 auto;
  }
}

@media (max-width: 575.98px) {
  .brand-grid {
    grid-template-columns: 1fr;
  }
}
</style>
